<template>
  <section class="location">
    <div v-if="isOrgActive && firstLoad">
      <LocationLoader />
    </div>

    <RouterView v-if="!firstLoad && isValidLocation" />

    <LocationInvalid v-if="!firstLoad && !isValidLocation" />

    <div class="disabledorg" v-if="!isOrgActive">
      <svg class="alerticon"><use xlink:href="#icon-alert-triangle"></use></svg>
      <p class="disabledorgcaption f5">{{ $t('Your organization is disabled.') }}</p>
      <p class="disabledorgcaption f5">
        <i18n path="Please go to your {operatorpanel} if you want to re-enable your account.">
          <a :href="operatorURL" target="_blank" slot="operatorpanel">{{$t('Operator Panel')}}</a>
        </i18n>
      </p>
    </div>
  </section>
</template>

<script>
import Component, { mixins } from 'vue-class-component';
import { checkEchoService } from '@/core/echo';
import LocationTimerMixin from './LocationTimerMixin';
import localizeMoment from '@/core/shared/localization/moment';
import localizeFlatpickr from '@/core/shared/localization/flatpickr';
// import { loadLocale } from '@/core/shared/localization';
import moment from 'moment-timezone';
import LocationSocketHelper from './LocationSocketHelper';
import AudioService from '@/core/AudioService';

import LocationLoader from './LocationLoader';
import LocationInvalid from './LocationInvalid';

@Component({
  components: {
    LocationLoader,
    LocationInvalid,
  },
  props: {
    locationId: {
      type: String,
      required: true,
    },
  },
  watch: {
    '$route' (newRoute, oldRoute) {
      if (
        (newRoute.name === 'overview' && oldRoute.name === 'overview') &&
        oldRoute.params.locationId !== newRoute.params.locationId
      ) {
        this.firstLoad = true;
        this.getData();
      }
    },
    calendarDate () {
      this.getLocationBookings();
      this.getLocationTemporaryClosings();
    },
    isNetworkAvailable (newVal, oldVal) {
      if (!newVal && oldVal) {
        this.$router.push({ name: 'overview' });
      }
    },
  },
})
export default class LocationRoute extends mixins(LocationTimerMixin) {
  firstLoad = true;
  operatorURL = `//${window.sbvrenv.OPERATOR_URL}`;

  get calendarDate () {
    return this.$store.getters.calendarDate;
  }

  get isOrgActive () {
    return this.$store.getters.isOrgActive;
  }

  get currentTime () {
    return this.$store.getters.currentTime;
  }

  get isNetworkAvailable () {
    return this.$store.getters.isNetworkAvailable;
  }

  get location () {
    return this.$store.getters['entities/Location'](this.locationId);
  }

  get experiences () {
    return this.$store.getters.locationExperiences(this.locationId);
  }

  get stations () {
    return this.$store.getters.locationStations(this.locationId);
  }

  get isValidLocation () {
    return this.location &&
      this.experiences.length > 0 &&
      this.experiences.some(experience => experience.tiers.length > 0) &&
      this.stations.length > 0;
  }

  created () {
    this.getData();

    this.$store.watch(state => state.timer.currentTime, (value, oldValue) => {
      if (!moment(value).isSame(moment(oldValue), 'day')) this.$store.commit('setTodaysDate', value);
    });
  }

  async getData () {
    if (!this.location) {
      setTimeout(() => { this.getData(); }, 1000);
      return;
    };

    if (!this.secondInterval && !this.minuteInterval) {
      this.createInterval();
    }

    moment.tz.setDefault(this.location.timezone);

    await Promise.all([
      localizeMoment(this.location.locale),
      localizeFlatpickr(this.location.locale),
      // loadLocale(this.$i18n, this.location.locale, 'monitor'),
      this.$store.dispatch('getCurrentTime'),
      this.$store.dispatch('getLocationExperiences', this.locationId),
      this.$store.dispatch('getLocationStations', this.locationId),
      this.$store.dispatch('getTags'),
      this.$store.dispatch('getStationClosings', { locationId: this.locationId }),
    ]);

    // Stop any previously calling stations when switching to new location
    AudioService.stopCalling();

    this.$store.dispatch('getContent');
    this.$store.commit('setCalendarDate', this.currentTime);

    this.setupRealTime();

    this.$store.dispatch('setLocationHeader');
    this.firstLoad = false;
  }

  getLocationBookings () {
    this.$store.dispatch('getLocationBookings', {
      locationId: this.locationId,
      startTime: this.calendarDate,
      endTime: moment(this.calendarDate).add(1, 'day').format(),
    });
  }

  getLocationTemporaryClosings () {
    this.$store.dispatch('getStationClosings', {
      locationId: this.locationId,
      startTime: this.calendarDate,
      endTime: moment(this.calendarDate).add(1, 'day').format(),
    });
  }

  async setupRealTime () {
    await checkEchoService();

    LocationSocketHelper.setUpLocationRealtime(
      this.location.id,
      station => {
        if (station.deletedAt) {
          this.$store.dispatch('removeEntities', { name: 'Station', payload: station });
        } else {
          this.$store.dispatch('getStation', station.id);
        }
      },
      stationDevice => {
        if (stationDevice.type === 'controller') {
          const payload = {
            id: stationDevice.stationId,
          };

          if (stationDevice.role === 'left_hand') {
            payload.leftController = {
              charging: stationDevice.charging,
              battery: stationDevice.battery,
            };
          }

          if (stationDevice.role === 'right_hand') {
            payload.rightController = {
              charging: stationDevice.charging,
              battery: stationDevice.battery,
            };
          }

          this.$store.dispatch('insertEntities', {
            name: 'Station',
            payload,
          });
        }
      },
      booking => {
        if (booking.deletedAt) {
          this.$store.dispatch('removeEntities', { name: 'Booking', payload: booking });
        } else {
          const bookingInStore = this.$store.getters['entities/Booking'](booking.id);
          const bookingHasLock = (bookingInStore && bookingInStore._lockWebSocketUpdates);
          const bookingStationTimeHasLock = (bookingInStore && bookingInStore.bookingStationTimes.every(bookingStationTime => bookingStationTime._lockWebSocketUpdates));
          if (!bookingInStore || !bookingHasLock || !bookingStationTimeHasLock) {
            this.$store.dispatch('getBooking', booking.id);
          }
        }
      },
      experience => {
        if (experience.deletedAt) {
          this.$store.dispatch('removeEntities', { name: 'Experience', payload: experience });
        } else {
          this.$store.dispatch('getExperience', experience.id);
        }
      },
    );
  }
}
</script>

<style lang="postcss" scoped>
@import "core/shared/styles";
.disabledorg {
  padding-top: var(--spacingLg);
  text-align: center;
}
.alerticon {
  width: 6rem;
  height: 6rem;
  fill: var(--colorSBRedFlat);
}
.disabledorgcaption {
  margin: 0;
  margin-top: var(--spacingXS);
}
</style>
